import { useTranslation } from 'react-i18next';
import type { ChangeEvent } from 'react';
import type { SignupFormErrors, SignupFormValues } from '../hooks/use-signup-form';

interface SignupPasswordFieldsProps {
  values: Pick<SignupFormValues, 'password' | 'passwordConfirm'>;
  errors: Pick<SignupFormErrors, 'password' | 'passwordConfirm'>;
  onChange: (field: 'password' | 'passwordConfirm', value: string) => void;
  onBlur: (field: 'password' | 'passwordConfirm') => void;
  disabled?: boolean;
}

export const SignupPasswordFields = ({
  values,
  errors,
  onChange,
  onBlur,
  disabled,
}: SignupPasswordFieldsProps) => {
  const { t } = useTranslation('auth');

  const passwordChange = (e: ChangeEvent<HTMLInputElement>) =>
    onChange('password', e.target.value);
  const confirmChange = (e: ChangeEvent<HTMLInputElement>) =>
    onChange('passwordConfirm', e.target.value);

  return (
    <>
      <div className="signup-page__field">
        <label className="login-page__label" htmlFor="signupPassword">
          {t('signup.fields.password')}
        </label>
        <div className="login-page__input-wrap">
          <input
            id="signupPassword"
            type="password"
            className={`login-page__input signup-page__input-no-icon${
              errors.password ? ' login-page__input--error' : ''
            }`}
            placeholder={t('signup.placeholders.password')}
            autoComplete="new-password"
            maxLength={64}
            value={values.password}
            onChange={passwordChange}
            onBlur={() => onBlur('password')}
            disabled={disabled}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'signupPasswordError' : undefined}
          />
        </div>
        {errors.password && (
          <p id="signupPasswordError" className="login-page__error" role="alert">
            {errors.password}
          </p>
        )}
      </div>

      <div className="signup-page__field">
        <label className="login-page__label" htmlFor="signupPasswordConfirm">
          {t('signup.fields.passwordConfirm')}
        </label>
        <div className="login-page__input-wrap">
          <input
            id="signupPasswordConfirm"
            type="password"
            className={`login-page__input signup-page__input-no-icon${
              errors.passwordConfirm ? ' login-page__input--error' : ''
            }`}
            placeholder={t('signup.placeholders.passwordConfirm')}
            autoComplete="new-password"
            maxLength={64}
            value={values.passwordConfirm}
            onChange={confirmChange}
            onBlur={() => onBlur('passwordConfirm')}
            disabled={disabled}
            aria-invalid={!!errors.passwordConfirm}
            aria-describedby={errors.passwordConfirm ? 'signupPasswordConfirmError' : undefined}
          />
        </div>
        {errors.passwordConfirm && (
          <p id="signupPasswordConfirmError" className="login-page__error" role="alert">
            {errors.passwordConfirm}
          </p>
        )}
      </div>
    </>
  );
};
